import { useState } from 'react'
import { User, Calendar, Clock, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { formatUserDisplayName } from '@/utils/formatters'

interface Owner {
  id: string
  display_name: string | null
  email: string | null
}

interface OwnerInfoCardProps {
  owner: Owner | null
  createdAt: string // ISO date string
  modifiedAt: string // ISO date string
  canTransfer: boolean
  onTransferClick: () => void
}

function formatDate(isoDate: string): string {
  try {
    const date = new Date(isoDate)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return isoDate
  }
}

export function OwnerInfoCard({
  owner,
  createdAt,
  modifiedAt,
  canTransfer,
  onTransferClick,
}: OwnerInfoCardProps) {
  const [isHovered, setIsHovered] = useState(false)

  const ownerName = formatUserDisplayName(owner)

  return (
    <div className="rounded-[14px] border border-[#f3f4f6] bg-[#f9fafb] p-4">
      <div className="space-y-4">
        {/* Owner row */}
        <div
          className="flex items-center justify-between"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="flex items-center gap-3">
            <div className="flex size-[36px] shrink-0 items-center justify-center rounded-[10px] bg-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.1)]">
              <User className="h-5 w-5 text-[#6a7282]" />
            </div>
            <div>
              <p className="text-[14px] text-[#6a7282]">Owner</p>
              <p className="text-[16px] font-semibold text-[#101828]">
                {ownerName}
              </p>
            </div>
          </div>
          {canTransfer && isHovered && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 text-primary hover:bg-accent hover:text-primary"
              onClick={onTransferClick}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Transfer
            </Button>
          )}
        </div>

        {/* Created row */}
        <div className="flex items-center gap-3">
          <div className="flex size-[36px] shrink-0 items-center justify-center rounded-[10px] bg-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.1)]">
            <Calendar className="h-5 w-5 text-[#6a7282]" />
          </div>
          <div>
            <p className="text-[14px] text-[#6a7282]">Created</p>
            <p className="text-[16px] font-semibold text-[#101828]">
              {formatDate(createdAt)}
            </p>
          </div>
        </div>

        {/* Modified row */}
        <div className="flex items-center gap-3">
          <div className="flex size-[36px] shrink-0 items-center justify-center rounded-[10px] bg-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.1)]">
            <Clock className="h-5 w-5 text-[#6a7282]" />
          </div>
          <div>
            <p className="text-[14px] text-[#6a7282]">Modified</p>
            <p className="text-[16px] font-semibold text-[#101828]">
              {formatDate(modifiedAt)}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
